import React, { useContext, useImperativeHandle, useMemo, useState } from 'react'

import { twoDigits } from '../../../../assets/ts/formatting/formatting'
import Buttons from '../../../../components/buttons/buttons'
import PageOverlayBusy from '../../../../components/page-overlay/page-overlay-busy'
import useProfileCount from '../../../../hooks/use-user-profile-count'
import useUserProfileViews from '../../../../hooks/use-user-profile-views'
import usePageAccumulator from '../../../../hooks/usePageAccumulator'
import { SettingsHeader } from '../../../../layouts/header/settings-header'
import { scrollContext } from '../../../../routers/router'
import { ALL_SELECT_VALUE_STRING } from '../../../../utils/constants/global-constants'
import NO_RESULTS from '../../../../utils/constants/no-results'
import InputSearch from '../../../../utils/controls/input-search'
import AddEditView from './add-edit-view'

const UserProfiles = () => {
  const contextRef = useContext(scrollContext)
  const [profilePageNum, setprofilePageNum] = useState(0)

  const [listModified, setListModified] = useState(false)
  const [searchFilter, setSearchFilter] = useState(ALL_SELECT_VALUE_STRING)

  const { userProfileView, userProfileViewIsLoading } = useUserProfileViews({
    isListModified: listModified,
    onePageOnly: true,
    pageNumber: profilePageNum,
    searchFilter: searchFilter,
  })
  const { profileCountIsLoading, profileCountValue } = useProfileCount({
    isListModified: listModified,
    searchFilter: searchFilter,
  })

  const [showAddPopup, setshowAddPopup] = useState(false)
  const [showEdit, showEditPopup] = useState(false)
  const [inEditMode, setInEditMode] = useState({
    rowKey: '',
    status: false,
  })
  /* Function call on edit */
  const onEdit = (item: ProfileItems) => {
    setshowAddPopup(false)
    showEditPopup(!showEdit)
    setInEditMode({
      rowKey: item.ItemKey,
      status: true,
    })
  }
  const onCancel = () => {
    setInEditMode({
      rowKey: '',
      status: false,
    })
    setshowAddPopup(false)
  }

  const accumulatedUserProfiles = usePageAccumulator(profilePageNum, userProfileView)
  useImperativeHandle(contextRef, () => ({
    onScroll() {
      if (profileCountValue > accumulatedUserProfiles.length) setprofilePageNum((prev) => prev + 1)
    },
  }))
  const profiles: ProfileItems[] = useMemo(
    () =>
      accumulatedUserProfiles.map((item) => ({
        DODIDnumber: item.DoDNumber,
        ItemKey: item.eKey,
        PASCODE: item.PASCODE,
        PASCODEServiceBy: item.PASCODEServicedBy,
        assignedComponentCode: item.AssignedComponentCode,
        dutyEmail: item.DutyEmail,
        dutyPhone: item.DutyPhone,
        eTag: item.eTag,
        installation: item.Installation,
        orgMAJCOM: item.OrgMAJCOM,
        organization: item.Organization,
        profileStatus: item.ProfileStatus,
        rank: item.Rank,
        servicingMAJCOM: item.ServicingMAJCOM,
        servicingOrganaization: item.ServicingOrganization,
        userDisplayName: item.UserDisplayName,
        userRid: item.UserRID,
      })),
    [accumulatedUserProfiles]
  )
  function onSearchChange(e: { target: { value: string } }) {
    setSearchFilter(e.target.value)
    setprofilePageNum(0)
  }
  return (
    <div className='row m-0'>
      {(userProfileViewIsLoading || profileCountIsLoading) && <PageOverlayBusy />}
      <SettingsHeader
        count={twoDigits(profileCountValue)}
        extras={
          <div className='d-flex align-items-center flex-wrap settingsfilters'>
            <div className='mx-2 my-sm-2'>
              <InputSearch
                className='border w-100 form-lg border-radius03  font-13 sourcesanspro max-w-400 min-w-400 maxfitcontent'
                formClassName=' form-vertical '
                icon='icon-search pe-2 title-color2 bold'
                inputProps={{
                  className: 'font-12 poppins-medium title-color',
                  id: 'Profilesearch',
                  maxLength: '500',
                  name: 'Profile search',
                  placeholder:
                    'Search with DoD, Duty Email, Assigned Location, Organization, PASCODE',
                  value: `${searchFilter}`,
                }}
                onChange={(e: any) => onSearchChange(e)}
                searchgroup=' w-100 d-flex align-items-center inputswithicon dash'
              />
            </div>
            <div className='dividerdashed vertical mx-1 mx-lg-2 d-none d-md-block'></div>
            <div className='ms-2 ms-sm-auto my-sm-2'>
              <Buttons
                aria-label='ADD'
                className='btn-sm font-0 font-xl-13 btn-primary ms-auto whitetext text-nowrap border-radius poppins-bold'
                icon='icon-add me-xl-1 font-11'
                label='ADD'
                onClick={() => {
                  setshowAddPopup(!showAddPopup)
                  setInEditMode({
                    rowKey: '',
                    status: false,
                  })
                }}
              />
            </div>
          </div>
        }
        name='User Profiles'
      />
      <div className='p-0 overflow-auto  settings-main mt-2'>
        {showAddPopup && (
          <AddEditView
            cancel={() => onCancel()}
            listModified={listModified}
            setListModified={setListModified}
            showAddPopup={showAddPopup}
            userProfile={userProfileView}
          />
        )}
        {profiles.length > 0 ? (
          profiles.map((profile, index) => (
            <div
              className='d-flex flex-column shadow card poppins-medium mb-2 py-3'
              key={profile.ItemKey}
            >
              <div className='d-flex px-2'>
                <div className='d-flex flex-column flex-md-row flex-wrap w-100 align-items-center'>
                  <div aria-live='polite' className='d-flex flex-column col my-2' tabIndex={0}>
                    <div className='title-color35 poppins-semibold font-12'>DoD ID Number</div>
                    <div className='poppins-semibold font-12 title-color2'>
                      {profile.DODIDnumber}
                    </div>
                  </div>
                  <div className='dividerdashed vertical mx-2 mx-lg-3 d-none d-md-block my-1'></div>
                  <div
                    aria-live='polite'
                    className='d-flex flex-column min-w-120 w-100 max-w-120 my-2'
                    tabIndex={0}
                  >
                    <div className='title-color35 poppins-semibold font-12'>Status</div>
                    <div className='poppins-semibold font-12 title-color2'>
                      {profile.profileStatus}
                    </div>
                  </div>
                  <div className='dividerdashed vertical mx-2 mx-lg-3 d-none d-md-block my-1'></div>
                  <div
                    aria-live='polite'
                    className='d-flex flex-column min-w-120 w-100 max-w-120 my-2'
                    tabIndex={0}
                  >
                    <div className='title-color35 poppins-semibold font-12'>Rank</div>
                    <div className='poppins-semibold font-12 title-color2'>{profile.rank}</div>
                  </div>
                  <div className='dividerdashed vertical mx-2 mx-lg-3 d-none d-md-block my-1'></div>
                  <div
                    aria-live='polite'
                    className='d-flex flex-column min-w-200 w-100 max-w-200 my-2'
                    tabIndex={0}
                  >
                    <div className='title-color35 poppins-semibold font-12'>Duty Email</div>
                    <div className='poppins-semibold font-12 title-color2'>{profile.dutyEmail}</div>
                  </div>
                  <div className='dividerdashed vertical mx-2 mx-lg-3 d-none d-md-block my-1'></div>
                  <div
                    aria-live='polite'
                    className='d-flex flex-column min-w-120 w-100 max-w-120 my-2'
                    tabIndex={0}
                  >
                    <div className='title-color35 poppins-semibold font-12'>Duty Phone</div>
                    <div className='poppins-semibold font-12 title-color2'>{profile.dutyPhone}</div>
                  </div>
                  <div className='dividerdashed vertical mx-2 mx-lg-3 d-none d-md-block my-1'></div>
                </div>
                <div className='d-flex align-items-center'>
                  <div className='d-flex gap-1 settings-actionbtns mx-2 mx-xxl-3'>
                    <Buttons
                      aria-label='Edit'
                      className={
                        inEditMode.status && inEditMode.rowKey === profile.ItemKey
                          ? 'btn-border btn-sm font-0 font-xl-14 btn-border-radius3 ms-auto bgcolor-primary whitetext text-nowrap sourcesansprosemibold active'
                          : 'btn-border btn-sm font-0 font-xl-14 btn-border-radius3 ms-auto color-primary text-nowrap sourcesansprosemibold'
                      }
                      icon='icon-pencil me-xl-1 font-10'
                      label='Edit'
                      onClick={() => onEdit(profile)}
                    />
                  </div>
                </div>
              </div>
              {inEditMode.status && inEditMode.rowKey === profile.ItemKey ? (
                <AddEditView
                  cancel={() => onCancel()}
                  clickedIndex={index}
                  listModified={listModified}
                  profile={profile}
                  setListModified={setListModified}
                  showEdit={showEdit}
                  userProfile={userProfileView}
                />
              ) : null}{' '}
            </div>
          ))
        ) : (
          <NO_RESULTS />
        )}
      </div>
    </div>
  )
}
export default UserProfiles
export interface ProfileItems {
  DODIDnumber: string
  ItemKey: string
  PASCODE: string
  PASCODEServiceBy: string
  assignedComponentCode: number
  dutyEmail: string
  dutyPhone: string
  eTag: string
  installation: string
  orgMAJCOM: string
  organization: string
  profileStatus: string
  rank: string
  servicingMAJCOM: string
  servicingOrganaization: string
  userDisplayName: string
  userRid: string
}
